/**
 * aC Men's Hub の課題管理のうち、Backlog の案件（ミラー `backlog_no_embedding.issues_raw` の写し）の語彙と API 契約。
 *
 * Backlog の状態から開発が要る課題の進捗を出す対応表、案件の「要確認」と「Backlogで確かめた」の判定、
 * ミラーの鮮度、Backlog 同期の実行記録の区分を、API（apps/api/ac-mens-hub）と Web で同じ定義から扱う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の Backlog 案件の状態対応表・開発要の課題の進捗・要確認と確かめた印・ミラーの鮮度・同期の実行記録の語彙を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
import { CaseLinkStateSchema, IssueIdSchema, IssueMutationResultSchema } from './issue-mgmt.js';
import { CaseAttentionSchema } from './issue-mgmt-status.js';
/**
 * ミラーの最新取得がこの時間より古ければ、画面に「Backlog の写しが更新されていません」を出す。
 * ミラーは1日1回の全件取り込みがあるので、1日＋余裕2時間を超えたら止まっているとみなす。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog ミラーの写しが古いと警告するまでの時間（26時間）を定める
 * @graph-connects none
 */
export const BACKLOG_MIRROR_STALE_HOURS = 26;
/**
 * 確認待ちの案件キーが、続けて何回の同期で見つからなければ要確認にするか。
 * 当日起案した案件はミラーに翌朝まで出ないので、1回では要確認にしない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 確認待ちの案件キーを要確認にするまでの、続けて見つからなかった同期の回数（2回）を定める
 * @graph-connects none
 */
export const CASE_MISSING_SYNC_LIMIT = 2;
/**
 * Backlog の状態「未対応」の名前（起案したばかりの案件。紐づけの候補にも使う）。
 * @graph-connects none
 */
export const BACKLOG_STATUS_NOT_STARTED = '未対応';
/**
 * Backlog の状態「保留中」の名前（対応中扱いだが要確認）。
 * @graph-connects none
 */
export const BACKLOG_STATUS_ON_HOLD = '保留中';
/**
 * 案件1件の進み具合の段。drafted＝起案済み（未対応）、in_progress＝対応中、done＝完了。
 * @graph-connects none
 */
export const CaseStageSchema = z.enum(['drafted', 'in_progress', 'done']);
/**
 * Backlog の状態名 → 案件の段と要確認の対応表。表に無い状態名は対応中扱い＋要確認にする。
 * 親課題用の状態は「保留中(親チケ用)」のように後ろに付くので、付いた部分を除いた名前で引く。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog の状態（未対応・処理中・開発完了・結合テスト完了・完了・保留中）を、課題の進捗の段（起案済み・対応中・完了）と要確認かどうかに対応させる
 * @graph-connects none
 */
export const BACKLOG_STATUS_STAGES = {
    [BACKLOG_STATUS_NOT_STARTED]: { stage: 'drafted', needsCheck: false },
    処理中: { stage: 'in_progress', needsCheck: false },
    開発完了: { stage: 'in_progress', needsCheck: false },
    結合テスト完了: { stage: 'in_progress', needsCheck: false },
    完了: { stage: 'done', needsCheck: false },
    [BACKLOG_STATUS_ON_HOLD]: { stage: 'in_progress', needsCheck: true },
};
/**
 * Backlog の状態名から、親課題用の印（「(親チケ用)」「（親チケ用）」）と前後の空白を除く。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog の親課題用の状態名を、対応表で引ける素の状態名にそろえる
 * @graph-connects none
 */
export function normalizeBacklogStatusName(name) {
    return name.replace(/[(（]親チケ用[)）]\s*$/u, '').trim();
}
/**
 * Backlog の状態名を、案件の段と要確認かどうかにする。known は対応表にある名前か。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog の状態名を対応表で引き、表に無い名前は対応中扱い＋要確認にする
 * @graph-connects normalizeBacklogStatusName [calls] 親課題用の印を除く
 */
export function classifyBacklogStatus(name) {
    const entry = BACKLOG_STATUS_STAGES[normalizeBacklogStatusName(name)];
    if (entry === undefined)
        return { stage: 'in_progress', needsCheck: true, known: false };
    return { ...entry, known: true };
}
/**
 * 写したミラーの状態名が、要確認か（状態名が読めない・保留中・対応表に無い状態名）。
 * 同期が紐づけを要確認にする判定と、確かめてもらう理由を出す判定の両方がこれを使う（書き分けると、要確認なのに理由が出ない紐づけができるため）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog 案件の状態名が要確認（読めない・保留中・対応表に無い）かを判定する
 * @graph-connects classifyBacklogStatus [calls] 状態の対応表
 */
export function backlogStatusNeedsCheck(statusName) {
    return statusName === null || classifyBacklogStatus(statusName).needsCheck;
}
/**
 * 開発が要る課題の進捗。not_started＝案件0件、drafted＝起案済み（全件未対応）、in_progress＝対応中、
 * done＝対応完了（全件完了）、resolved＝解決確認済み（人が直ったと確かめた）。
 * @graph-connects none
 */
export const IssueDevProgressSchema = z.enum([
    'not_started',
    'drafted',
    'in_progress',
    'done',
    'resolved',
]);
/**
 * 紐づく案件の Backlog の状態から、開発が要る課題の進捗を出す（人は変えられない。解決確認済みだけ人が付ける）。
 *
 * 削除された案件は数えない。まだミラーで見つかっていない案件（確認待ち・見つからない）は起案済みとして数える。
 * 0件なら未着手、全件未対応なら起案済み、全件完了なら対応完了、それ以外は対応中。
 * incomplete は完了していない案件の数（判定を変えるときの補足の要否に使う）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に紐づく Backlog 案件の状態から、開発が要る課題の進捗（未着手・起案済み・対応中・対応完了・解決確認済み）と完了・未完了の件数を出す
 * @graph-connects classifyBacklogStatus [calls] 状態の対応表
 */
export function deriveDevProgress(input) {
    const stages = input.cases
        .filter((c) => !c.deleted)
        .map((c) => (c.statusName === null ? 'drafted' : classifyBacklogStatus(c.statusName).stage));
    const total = stages.length;
    const completed = stages.filter((s) => s === 'done').length;
    const counts = { total, completed, incomplete: total - completed };
    if (input.resolvedAt !== null)
        return { progress: 'resolved', ...counts };
    if (total === 0)
        return { progress: 'not_started', ...counts };
    if (completed === total)
        return { progress: 'done', ...counts };
    if (stages.every((s) => s === 'drafted'))
        return { progress: 'drafted', ...counts };
    return { progress: 'in_progress', ...counts };
}
/**
 * 案件を確かめてもらう理由と、「Backlogで確かめた」の印で今は出さないか（checked）を決める。
 *
 * 確認待ち（紐づけた直後と、見つからない・削除の案件で「確かめた」を押した後）は次の同期を待つので出さない。
 * 削除・続けて見つからない → 印は効かない（直すまで出し続ける）。状態名が読めない・保留中・表に無い状態名・期限超過は、
 * 印を付けた時点から状態名と期限日のどちらも変わっていなければ出さない。期限超過は日本時間の今日より前の期限日で、
 * 完了した案件には出さない。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog 案件の要確認（見つからない・削除・状態名が読めない・保留中・表に無い状態）と期限超過を判定し、Backlogで確かめた印が効いているかを決める
 * @graph-connects backlogStatusNeedsCheck [calls] 状態名が要確認か
 * @graph-connects classifyBacklogStatus [calls] 状態の対応表
 * @graph-connects normalizeBacklogStatusName [calls] 保留中の判定
 */
export function deriveCaseAttention(input, today) {
    if (input.state === 'pending')
        return { attention: null, checked: false };
    if (input.deleted)
        return { attention: 'deleted', checked: false };
    if (input.missCount >= CASE_MISSING_SYNC_LIMIT)
        return { attention: 'not_found', checked: false };
    if (!input.copied)
        return { attention: null, checked: false };
    let attention = null;
    if (backlogStatusNeedsCheck(input.statusName)) {
        if (input.statusName === null)
            attention = 'missing_status';
        else if (normalizeBacklogStatusName(input.statusName) === BACKLOG_STATUS_ON_HOLD)
            attention = 'on_hold';
        else
            attention = 'unknown_status';
    }
    else if (input.statusName !== null &&
        classifyBacklogStatus(input.statusName).stage !== 'done' &&
        input.dueDate !== null &&
        input.dueDate < today) {
        attention = 'overdue';
    }
    if (attention === null)
        return { attention: null, checked: false };
    const checked = input.check !== null &&
        input.check.statusName === input.statusName &&
        input.check.dueDate === input.dueDate;
    return { attention, checked };
}
/**
 * ミラーの鮮度。最新取得からの時間（時間単位・切り捨て）と、警告を出すか。
 * 一度も写していない（最新取得が分からない）ときも警告を出す（止まっていても気づけるように）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog ミラーの最新取得時刻から、何時間更新されていないかと、26時間を超えて警告を出すかを決める
 * @graph-connects none
 */
export function deriveMirrorFreshness(mirrorFetchedAt, now) {
    if (mirrorFetchedAt === null)
        return { staleHours: null, stale: true };
    const elapsedMs = now.getTime() - Date.parse(mirrorFetchedAt);
    return {
        staleHours: Math.max(0, Math.floor(elapsedMs / 3_600_000)),
        stale: elapsedMs > BACKLOG_MIRROR_STALE_HOURS * 3_600_000,
    };
}
/**
 * 課題管理の定期処理の種類（`issue__job_runs.job`）。
 * @graph-connects none
 */
export const IssueJobSchema = z.enum(['slack_ingest', 'backlog_sync', 'stale_notify']);
/**
 * 定期処理の起動のきっかけ。scheduled＝定期、manual＝画面の「今すぐ実行」。
 * @graph-connects none
 */
export const IssueJobTriggerSchema = z.enum(['scheduled', 'manual']);
/**
 * 定期処理を終えたときの結果。success＝全部処理できた、partial＝一部失敗、failed＝何も処理できなかった。
 * @graph-connects none
 */
export const IssueJobRunStatusSchema = z.enum(['success', 'partial', 'failed']);
/**
 * 定期処理の失敗の区分コード（`issue__job_runs.run_failure_code`）。例外のメッセージなどの自由文は持たない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の定期処理の失敗の区分（認証失敗・設定不備・チャンネル未参加・回数制限・時間切れ・相手の API のエラー・読めない応答・保存の失敗・不明）
 * @graph-connects none
 */
export const IssueJobFailureCodeSchema = z.enum([
    'auth_failed',
    'config_missing',
    'not_in_channel',
    'rate_limited',
    'timeout',
    'api_error',
    'parse_failed',
    'write_failed',
    'unknown',
]);
/**
 * 課題の案件欄の1行（紐づけと、ミラーの写し）。写しがまだ無ければ写しの項目は null。
 * - attention / checked: 確かめてもらう理由と、Backlogで確かめた印で今は出さないか（`deriveCaseAttention`）
 * - syncedAt: この案件の写しを最後に書いた時刻
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に紐づく Backlog 案件1件（キー・紐づけの状態・件名・状態・担当・期限・リリース完了日・親課題・削除・確かめてもらう理由・確かめた印）
 * @graph-connects none
 */
export const IssueCaseSchema = z.object({
    caseKey: z.string(),
    state: CaseLinkStateSchema,
    title: z.string().nullable(),
    statusName: z.string().nullable(),
    stage: CaseStageSchema.nullable(),
    assigneeName: z.string().nullable(),
    dueDate: z.string().nullable(),
    releasedDate: z.string().nullable(),
    parentIssueId: z.number().int().nullable(),
    isMens: z.boolean().nullable(),
    deleted: z.boolean(),
    syncedAt: z.string().nullable(),
    attention: CaseAttentionSchema.nullable(),
    checked: z.boolean(),
    checkedAt: z.string().nullable(),
    checkedBy: z.string().nullable(),
});
/**
 * 課題の案件欄を引く条件。
 * @graph-connects none
 */
export const IssueCasesQuerySchema = z.object({ issueId: IssueIdSchema });
/**
 * 課題の案件欄の応答。progress は開発が要る課題の進捗（開発が要らない・判定前の課題でも案件から計算した値を返す）。
 * unresolvedAt は未解決の印（まだ直っていない・再発した日。新しい案件を紐づけると消える）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の案件欄（紐づく Backlog 案件の写しと、進捗・完了件数・未完了件数・未解決の印）の応答
 * @graph-connects none
 */
export const IssueCasesResponseSchema = z.object({
    issueId: z.string(),
    progress: IssueDevProgressSchema,
    total: z.number().int(),
    completed: z.number().int(),
    incomplete: z.number().int(),
    unresolvedAt: z.string().nullable(),
    cases: z.array(IssueCaseSchema),
});
/**
 * Backlog 同期の最後の実行。
 * @graph-connects none
 */
export const BacklogSyncLastRunSchema = z.object({
    jobRunId: z.string(),
    trigger: IssueJobTriggerSchema,
    triggeredBy: z.string().nullable(),
    startedAt: z.string(),
    finishedAt: z.string(),
    runStatus: IssueJobRunStatusSchema,
    failureCode: IssueJobFailureCodeSchema.nullable(),
    failedElements: z.array(z.string()),
    processedCount: z.number().int(),
});
/**
 * Backlog 同期の状況（画面の鮮度の帯・最終同期・今すぐ同期の押せる／押せない）。
 * - mirrorFetchedAt: 同期で見たミラーの最新取得時刻。mirrorStale は 26 時間を超えたか、一度も同期していない
 * - running: 今実行中か（実行中は今すぐ同期を押させない）
 * - lastSyncedAt: 最後に写しを書けた実行（success か partial）を終えた時刻。案件欄の「最終同期」
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog 同期の状況（ミラーの最新取得と鮮度の警告・実行中か・最後の実行の結果・最終同期の時刻）
 * @graph-connects none
 */
export const BacklogSyncStatusSchema = z.object({
    mirrorFetchedAt: z.string().nullable(),
    mirrorStaleHours: z.number().int().nullable(),
    mirrorStale: z.boolean(),
    running: z.boolean(),
    lastRun: BacklogSyncLastRunSchema.nullable(),
    lastSyncedAt: z.string().nullable(),
});
/**
 * 今すぐ同期の結果。completed は終えた実行の結果、already_running は別の実行が動いていて始めなかった。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog の今すぐ同期の結果（終えた実行の結果と失敗した案件キー、または実行中で始めなかったこと）
 * @graph-connects none
 */
export const BacklogSyncRunResultSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('completed'),
        jobRunId: z.string(),
        runStatus: IssueJobRunStatusSchema,
        failureCode: IssueJobFailureCodeSchema.nullable(),
        failedElements: z.array(z.string()),
        processedCount: z.number().int(),
    }),
    z.object({ outcome: z.literal('already_running'), startedAt: z.string() }),
]);
/**
 * 案件を紐づける小窓の候補1件（Backlog の新しいメンズ案件）。
 * @graph-connects none
 */
export const CaseCandidateSchema = z.object({
    caseKey: z.string(),
    title: z.string().nullable(),
    statusName: z.string().nullable(),
    createdAt: z.string().nullable(),
});
/**
 * 案件を紐づける小窓の候補の応答（未対応のメンズの親課題を、作成日の新しい順に）。
 * @graph-connects none
 */
export const CaseCandidatesResponseSchema = z.object({ candidates: z.array(CaseCandidateSchema) });
/**
 * 「Backlogで確かめた」の結果。recorded が false なら、見つからない・削除された案件で印を付けず、
 * 次の同期で取り直す（まだ無ければまた要確認になる）。
 * @graph-connects none
 */
export const IssueCaseCheckResultSchema = IssueMutationResultSchema.extend({
    recorded: z.boolean(),
});
//# sourceMappingURL=issue-mgmt-backlog.js.map